import React, { useEffect, useRef, useState } from 'react';
import type { QuickPeriod } from '../lib/quickDateRange';
import { QUICK_PERIODS } from '../lib/quickDateRange';
import type { DateRule } from '../../../services/pmReportPresetService';

interface SavePresetDialogProps {
    open: boolean;
    onSave: (input: { name: string; dateRule: DateRule }) => void;
    onCancel: () => void;
    existingNames: string[];
    activeQuickPreset: QuickPeriod | null;
    absoluteRange: { startDate: string; endDate: string };
}

export const SavePresetDialog: React.FC<SavePresetDialogProps> = ({
    open, onSave, onCancel, existingNames, activeQuickPreset, absoluteRange,
}) => {
    const [name, setName] = useState('');
    const [kind, setKind] = useState<'rolling' | 'absolute'>(activeQuickPreset ? 'rolling' : 'absolute');
    const [error, setError] = useState<string | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (open) {
            setName('');
            setError(null);
            setKind(activeQuickPreset ? 'rolling' : 'absolute');
            inputRef.current?.focus();
        }
    }, [open, activeQuickPreset]);

    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, onCancel]);

    if (!open) return null;

    const trimmed = name.trim();
    const canAttemptSave = trimmed.length > 0;

    const validate = (): string | null => {
        if (!trimmed) return 'Preset name is required';
        if (trimmed.length > 40) return 'Preset name must be 40 characters or fewer';
        const lower = trimmed.toLowerCase();
        if (existingNames.some(n => n.toLowerCase() === lower)) return `A preset named "${trimmed}" already exists`;
        return null;
    };

    const handleSave = () => {
        const err = validate();
        if (err) { setError(err); return; }
        const dateRule: DateRule =
            kind === 'rolling' && activeQuickPreset
                ? { kind: 'rolling', preset: activeQuickPreset }
                : { kind: 'absolute', startDate: absoluteRange.startDate, endDate: absoluteRange.endDate };
        onSave({ name: trimmed, dateRule });
    };

    const rollingLabel = activeQuickPreset
        ? QUICK_PERIODS.find(p => p.key === activeQuickPreset)?.label ?? activeQuickPreset
        : '—';

    return (
        <div role="dialog" aria-modal="true" className="rounded-lg border border-slate-200 bg-white p-3 shadow-lg dark:border-slate-700 dark:bg-slate-800">
            <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                Name
                <input
                    ref={inputRef}
                    type="text"
                    value={name}
                    onChange={(e) => { setName(e.target.value); setError(null); }}
                    onKeyDown={(e) => { if (e.key === 'Enter' && canAttemptSave) handleSave(); }}
                    className="mt-1 w-full rounded-md border border-slate-200 bg-slate-50 px-2 py-1.5 text-xs"
                />
            </label>

            <fieldset className="mt-2">
                <legend className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Date</legend>
                <label className="mt-1 flex items-center gap-2 text-xs">
                    <input
                        type="radio"
                        name="date-rule"
                        checked={kind === 'rolling'}
                        disabled={!activeQuickPreset}
                        onChange={() => setKind('rolling')}
                    />
                    Use rolling preset ({rollingLabel}) — recomputes each time
                </label>
                <label className="mt-1 flex items-center gap-2 text-xs">
                    <input
                        type="radio"
                        name="date-rule"
                        checked={kind === 'absolute'}
                        onChange={() => setKind('absolute')}
                    />
                    Use exact dates ({absoluteRange.startDate} – {absoluteRange.endDate})
                </label>
            </fieldset>

            {error && <p role="alert" className="mt-2 text-xs text-red-600">{error}</p>}

            <div className="mt-3 flex justify-end gap-2">
                <button type="button" onClick={onCancel} className="rounded-md border border-slate-300 px-3 py-1.5 text-xs">
                    Cancel
                </button>
                <button
                    type="button"
                    onClick={handleSave}
                    disabled={!canAttemptSave}
                    className="rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
                >
                    Save
                </button>
            </div>
        </div>
    );
};
